import { Avatar, styled } from '@mui/material';
import { forwardRef, useEffect, useState } from 'react';
import userService from 'services/user';
import Text from 'shell/components/Text';
import { getS3FileURL } from 'utils/helper';

const CustomAvatar = styled(Avatar)(({ $bgColor, size, $textColor, theme }) => {
    const mappedSize = {
        small: '1.5rem',
        medium: '2.5rem',
        large: '3.5rem',
        'x-large': '6rem',
    }[size];

    return {
        width: mappedSize,
        height: mappedSize,
        background: $bgColor || theme.palette.ensightful.error[800],
        color: $textColor || theme.palette.ensightful.shades[0],
    };
});

const getName = (user) => {
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    const preferredName = user?.display_name || '';
    if (preferredName) return `${preferredName} ${lastName}`;
    return `${firstName} ${lastName}`;
};

const getInitials = (user) => {
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    const preferredName = user?.display_name || '';
    let initials = preferredName
        ? `${preferredName?.charAt(0)}${lastName?.charAt(0)}`
        : `${firstName.charAt(0)}${lastName?.charAt(0)}`;
    return initials.toUpperCase();
};

const SIZES = ['small', 'medium', 'large', 'x-large'];

export default forwardRef(({ size = 'small', user, ...rest }, ref) => {
    if (!SIZES.includes(size)) throw new Error(`Shell: Invalid size "${size}" in component Avatar.`);

    const mappedVariant = {
        small: 'p3',
        medium: 'p1',
        large: 'h6',
        'x-large': 'h3',
    }[size];
    const [profileImageUrl, setProfileImageUrl] = useState(user?.profile_image?.preview_url || '');
    const userId = user?.id;
    const profileImage = user?.profile_image;
    const bucket = profileImage?.bucket;
    const key = profileImage?.key;
    const name = profileImage?.name;
    const previewUrl = profileImage?.preview_url;

    useEffect(() => {
        let active = true;
        if (previewUrl) {
            setProfileImageUrl(previewUrl);
            return () => {
                active = false;
            };
        }

        if (!bucket || !key || !name) {
            setProfileImageUrl('');
            return () => {
                active = false;
            };
        }

        if (!userId) {
            setProfileImageUrl(getS3FileURL({ bucket, key, name }));
            return () => {
                active = false;
            };
        }

        setProfileImageUrl('');
        userService
            .createProfileImageUrl(userId, { bucket, key, name })
            .then(({ url }) => {
                if (active) setProfileImageUrl(url);
            })
            .catch((error) => {
                console.error('Failed to load profile image.', error);
                if (active) setProfileImageUrl(getS3FileURL({ bucket, key, name }));
            });

        return () => {
            active = false;
        };
    }, [bucket, key, name, previewUrl, userId]);

    return (
        <CustomAvatar
            ref={ref}
            alt={getName(user)}
            $bgColor={user?.profile_circle?.bg_color}
            size={size}
            src={profileImageUrl}
            $textColor={user?.profile_circle?.text_color}
            {...rest}
        >
            <Text color={'inherit'} component={'span'} variant={mappedVariant}>
                {getInitials(user)}
            </Text>
        </CustomAvatar>
    );
});
